import React, { useEffect, useMemo, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { SearchTextField } from "../lib";
import { useTranslation } from "react-i18next";
import { Cancel, ExpandLess, ExpandMore, FilterList } from "@mui/icons-material";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Layout from "../shared/components/LayoutComponent";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import BlockIcon from "@mui/icons-material/Block";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import AnhangListe from "./VstComponents/AnhangListe";

const VstUebersicht = ({ list, onAction, userAuswahl, expanded, setExpanded }) => {
  const { t } = useTranslation();
  const [suche, setSuche] = useState("");
  const [eventArt, setEventArt] = useState([]);
  const [pflichtVeranstaltung, setPflichtVeranstaltung] = useState(null);
  const [termin, setTermin] = useState(dayjs().subtract(1, "month").startOf("month"));
  const [filteredList, setFilteredList] = useState([]);
  const [filterDialog, setFilterDialog] = useState(false);
  const [mobileDetails, setMobileDetails] = useState(false);
  const [selectedVst, setSelectedVst] = useState([]);
  const [selectedEvt, setSelectedEvt] = useState([]);

  const eventArtOptions = useMemo(() => {
    // Herrausfiltern der einzelnen Optionen
    const tmpfilter = list
      ?.filter((value, index, array) => array.findIndex((element) => element.bezeichnung === value.bezeichnung) === index)
      .map((mapElement) => {
        return {
          value: mapElement.key,
          label: mapElement.bezeichnung,
        };
      })
      ?.filter((item) => item !== null);
    return tmpfilter ? tmpfilter : [];
  }, [list]);

  const pflichtVeranstaltungOptions = [
    { value: "0", label: "Nein" },
    { value: "1", label: "Ja" },
  ];

  //------------------------------------------------------
  //-----------Allgemeine FUnktionen----------------------
  //------------------------------------------------------

  // Filter / Suchfunktion
  useEffect(() => {
    const result = list
      ? list
          .map((item) => {
            const tmpEventArt = eventArt?.length !== 0 ? eventArt?.some((searchItem) => searchItem.label === item.bezeichnung) : true;
            const tmpPflichtVeranstaltung = !pflichtVeranstaltung ? true : pflichtVeranstaltung.value === "0" ? !item?.faellig : item?.faellig;
            const tmpSuche =
              suche?.trim() !== "" ? [item.beschreibung, item.bezeichnung].some((field) => field?.toLowerCase().includes(suche.toLowerCase())) : true;

            // Wenn einer der Filter nicht passt, Event ausschließen
            if (!tmpEventArt || !tmpPflichtVeranstaltung || !tmpSuche) {
              return null;
            }

            // 2. Termin-Filter & Transformation der Unterveranstaltungen
            let neueVeranstaltungen = item.veranstaltung;

            if (termin && item.veranstaltung) {
              const terminNum = Number(termin.format("YYYYMMDD"));

              // Nur Unterveranstaltungen behalten, die >= dem gewählten Termin sind
              neueVeranstaltungen = item.veranstaltung.filter((v) => Number(v.termin) >= terminNum);

              // Wenn nach dem Filter keine Unterveranstaltungen mehr übrig sind,
              // wird das Haupt-Event komplett ausgeschlossen
              if (neueVeranstaltungen.length === 0) {
                return null;
              }
            }

            // 3. Neues Objekt zurückgeben
            return {
              ...item,
              veranstaltung: neueVeranstaltungen,
            };
          })
          .filter(Boolean)
      : []; // Boolean filtert die `null`-Werte heraus

    setFilteredList(result);
  }, [list, suche, eventArt, pflichtVeranstaltung, termin]);

  const CustomAccordionSummary = styled((props) => <AccordionSummary {...props} />)(({ theme }) => ({
    flexDirection: "row-reverse",
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
      transform: "rotate(90deg)",
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
      marginTop: theme.spacing(3),
    },
    [`& .${accordionSummaryClasses.content}`]: {
      marginLeft: theme.spacing(1),
    },
  }));

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  //------------------------------------------------------
  //---------Handy relevante Funktionen-------------------
  //------------------------------------------------------

  const EventsMobile = () => {
    return (
      <Grid container spacing={2} padding={2}>
        {filteredList?.map((evt, index) => {
          return (
            <Box sx={{ display: "flex", width: "100%", padding: 2 }}>
              <Card sx={{ width: "100%" }}>
                <CardContent>
                  <Typography sx={{ fontWeight: "bold" }} component="span">
                    {evt.bezeichnung}
                    {evt.faellig && (
                      <Tooltip sx={{ marginTop: "2px", marginLeft: "10px" }} title={t("Teilnahme erforderlich")}>
                        <PriorityHighIcon color="error" />
                      </Tooltip>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    {evt?.warteliste && (
                      <IconButton>
                        <Tooltip title={t("Auf Warteliste")}>
                          <PlaylistAddCheckIcon />
                        </Tooltip>
                      </IconButton>
                    )}

                    {evt.anmeldung != "3" &&
                      !evt.warteliste &&
                      ["0", "2", "5"].includes(evt.anmeldung) &&
                      !evt.veranstaltung?.find((v) => v.status === "0") && (
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAction("interesse", evt.eventnr + "000");
                          }}
                        >
                          <Tooltip title={t("Warte-Liste beitreten")}>
                            <AddIcon />
                          </Tooltip>
                        </IconButton>
                      )}

                    {evt.warteliste && (
                      <IconButton
                        variant="icon"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAction("abmelden", evt.eventnr + "000");
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "center" }}>
                          <Tooltip title={t("Warte-Liste austreten")}>
                            <CloseIcon />
                          </Tooltip>
                        </Box>
                      </IconButton>
                    )}
                  </Box>

                  <Box display="flex" width="100%">
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <List>
                        {evt?.veranstaltung?.length > 0 ? (
                          evt?.veranstaltung?.map((veranstaltung, index) => {
                            const tmpNum = index + 1;
                            return (
                              <>
                                <Divider />
                                <ListItem disablePadding>
                                  <ListItemText
                                    primary={
                                      <Typography sx={{ display: "flex", alignItems: "center" }}>
                                        <Chip
                                          sx={{ margin: 1 }}
                                          label={tmpNum}
                                          color={veranstaltung.status === "0" ? "warning" : veranstaltung.status === "1" ? "primary" : "error"}
                                        ></Chip>
                                        {veranstaltung?.info}

                                        {/* Für jeden Veranstaltungsstatus ein Icon */}
                                        {veranstaltung?.anmeldung && <StatusIcon status={veranstaltung?.anmeldung}></StatusIcon>}
                                      </Typography>
                                    }
                                    secondary={
                                      <>
                                        <Typography>{veranstaltung.terminText + " " + veranstaltung.zeitText + " - " + veranstaltung.endeText}</Typography>
                                      </>
                                    }
                                    onClick={() => detailsChange(veranstaltung, evt)}
                                  />
                                </ListItem>
                                <Divider />
                              </>
                            );
                          })
                        ) : (
                          <Typography>{t("Keine Veranstaltungen geplant")}</Typography>
                        )}
                      </List>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Grid>
    );
  };

  const StatusIcon = ({ status }) => {
    switch (status) {
      case "0":
        return (
          <Tooltip sx={{ marginLeft: "auto" }} title="Angemeldet">
            <HowToRegIcon color="primary" />
          </Tooltip>
        );
        break;
      case "1":
        return (
          <Tooltip sx={{ marginLeft: "auto" }} title="Teilnahme bestätigt">
            <CheckCircleIcon color="primary" />
          </Tooltip>
        );
        break;
      case "2":
        return (
          <Tooltip sx={{ marginLeft: "auto" }} title="Abwesenheit bestätigt">
            <DoNotDisturbOnIcon color="error" />
          </Tooltip>
        );
        break;
      case "5":
        return (
          <Tooltip sx={{ marginLeft: "auto" }} title="Teilnahme (vorgemerkt)">
            <ScheduleIcon color="primary" />
          </Tooltip>
        );
        break;
      case "6":
        return (
          <Tooltip sx={{ marginLeft: "auto" }} title="Abwesenheit (vorgemerkt)">
            <EventBusyIcon color="error" />
          </Tooltip>
        );
        break;
      case "8":
        return (
          <Tooltip sx={{ marginLeft: "auto" }} title="abgesagt (keine teilnahme)">
            <BlockIcon color="error" />
          </Tooltip>
        );
        break;
      case "9":
        return (
          <Tooltip sx={{ marginLeft: "auto" }} title="angemeldet (unbestätigt)">
            <PendingActionsIcon color="primary" />
          </Tooltip>
        );
        break;
      default:
        return null;
    }
    return null;
  };

  // --------------------
  // --Details relevant--
  // --------------------
  const detailsChange = (vst, evt) => {
    setMobileDetails(true);
    setSelectedVst(vst);
    setSelectedEvt(evt);
  };

  const CardDatenMobile = ({ vst, evt }) => {
    return (
      <List>
        <ListItem>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight="bold">{t("Event Status")}</Typography>
            <Typography color={vst.status === "0" ? "orange" : vst.status === "1" ? "green" : "red"}>{vst.statusText}</Typography>
          </Box>
        </ListItem>

        <ListItem>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight="bold">{t("Termin")}</Typography>
            <Typography>{vst.terminText}</Typography>
            <Typography>{vst.zeitText + " - " + vst.endeText}</Typography>
            <Typography>{vst.dauer + " Min."}</Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <IconButton
              color="primary"
              onClick={() => {
                onAction("termin_download", vst);
              }}
            >
              <EventIcon />
            </IconButton>
          </Box>
        </ListItem>

        {vst?.plz && (
          <ListItem>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography fontWeight="bold">{t("Veranstaltungsort")}</Typography>
              <Typography>{vst.str + " " + vst.hnr}</Typography>
              <Typography>{vst.plz + " " + vst.ort}</Typography>
              <Typography>{vst.ldkzText}</Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <IconButton
                color="primary"
                onClick={() => {
                  window.open(
                    "https://www.google.com/maps/search/?api=1&query=" + vst.ldkzText + "+" + vst.plz + "+" + vst.ort + "+" + vst.str + "+" + vst.hnr,
                  );
                }}
              >
                <LocationOnIcon />
              </IconButton>
            </Box>
          </ListItem>
        )}

        {vst?.anhang?.length > 0 && <AnhangListe vst={vst} onAction={onAction} t={t} />}

        {/* {vst?.einladung && (
          <ListItem>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography fontWeight="bold">{t("Anhang")}</Typography>
              <Typography sx={{ wordBreak: "break-word" }}>{vst.einladungText}</Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <IconButton
                color="primary"
                onClick={() => {
                  onAction("einladung_download", vst);
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Box>
          </ListItem>
        )} */}

        <ListItem>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight="bold">{t("Anmeldungen")}</Typography>
            <Typography>{vst.anzahlTeilnehmer + (evt.tnMax === 0 ? "" : " (max." + evt.tnMax + t(" Teilnehmer"))}</Typography>

            {vst.anmeldung && (
              <Typography color={["0", "1", "5", "9"].includes(vst.anmeldung) ? "green" : "red"}>{vst.anmeldung && vst.anmeldungText}</Typography>
            )}
            {/* {vst.anmeldung && vst.anmeldung !== "9" && <Typography color="green">Sie sind zu dieser Veranstaltung angemeldet</Typography>} */}
            {!vst.anmeldung && evt.tnMax > 0 && vst.anzahlTeilnehmer >= evt.tnMax && <Typography color="red">maximale Teilnehmerzahl erreicht</Typography>}
          </Box>
        </ListItem>

        <ListItem>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight="bold">{t("Verantwortlicher")}</Typography>
            <Typography>{evt.kontaktName}</Typography>
          </Box>
        </ListItem>

        <ListItem>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography fontWeight="bold">{"Leiter/Trainer"}</Typography>
            <Typography>{vst.kontaktName}</Typography>
          </Box>
        </ListItem>
      </List>
    );
  };

  const CardActionsMobile = ({ vst, evt, onClose }) => {
    return (
      <>
        <Button style={{ marginLeft: "40px" }} variant="text" color="primary" onClick={() => onClose()}>
          {t("Schlie­ßen")}
        </Button>
        <Box sx={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between" }}>
          <Box sx={[]}></Box>
          <Box sx={[]}>
            {!["2", "3"].includes(evt.anmeldung) && vst.status === "0" && (
              <>
                {!vst.anmeldung && (
                  <IconButton
                    variant="icon"
                    color="primary"
                    tooltip="an Veranstaltung teilnehmen"
                    onClick={(e) => {
                      actionHandle(e, "anmelden", vst.vstnr);
                      onClose();
                    }}
                  >
                    <AddIcon />
                    <Typography>{"Teilnehmen"}</Typography>
                  </IconButton>
                )}

                {vst.anmeldung && (
                  <IconButton
                    variant="icon"
                    color="error"
                    tooltip="abmelden"
                    onClick={(e) => {
                      actionHandle(e, "abmelden", vst.vstnr);
                      onClose();
                    }}
                  >
                    <CloseIcon />
                    <Typography>{"Abmelden"}</Typography>
                  </IconButton>
                )}
              </>
            )}

            {vst.anmeldung != "3" && (
              // Interesse / Teilnahme
              <>
                {evt.anmeldung === "3" && vst.status === "0" && (
                  <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "end" }}>
                    {vst.anmeldung !== "0" && (
                      <IconButton
                        variant="icon"
                        color="primary"
                        tooltip="zu Veranstaltung zusagen"
                        onClick={(e) => {
                          actionHandle(e, "anmelden", vst.vstnr);
                          onClose();
                        }}
                      >
                        <AddIcon />
                        <Typography>{"Zusagen"}</Typography>
                      </IconButton>
                    )}

                    {vst.anmeldung !== "8" && (
                      <IconButton
                        variant="icon"
                        tooltip="abmelden"
                        color="error"
                        onClick={(e) => {
                          actionHandle(e, "abmelden", vst.vstnr);
                          onClose();
                        }}
                      >
                        <CloseIcon />
                        <Typography>{"Absagen"}</Typography>
                      </IconButton>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>

        {vst.status === "1" && vst.anmeldung && !["1", "2"].includes(vst.anmeldung) && (
          // teilgenommen / nicht teilgenommen
          <>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography>Haben Sie teilgenommen?</Typography>
              <Box display="flex" justifyContent="space-between" width="100%">
                <IconButton
                  variant="icon"
                  tooltip="Teilnahme bestätigen"
                  color="primary"
                  onClick={(e) => {
                    actionHandle(e, "teilgenommen", vst.vstnr);
                    onClose();
                  }}
                >
                  <CheckIcon />
                </IconButton>

                <IconButton
                  variant="icon"
                  tooltip="nicht teilgenommen"
                  color="error"
                  onClick={(e) => {
                    actionHandle(e, "gefehlt", vst.vstnr);
                    onClose();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </>
    );
  };

  const DetailsEvt = ({ open, onClose, onOpen }) => {
    return (
      <Dialog open={open} onClose={() => onClose()} fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Details {selectedVst?.info}</DialogTitle>
        <DialogContent>
          <CardDatenMobile vst={selectedVst} evt={selectedEvt}></CardDatenMobile>
        </DialogContent>
        <DialogActions>
          <CardActionsMobile vst={selectedVst} evt={selectedEvt} onClose={onClose}></CardActionsMobile>
        </DialogActions>
      </Dialog>
    );
  };

  const actionHandle = (event, action, variable) => {
    event.stopPropagation();
    onAction(action, variable);
  };

  //------------------------------------------------------
  //--------Desktop relevante Funktionen------------------
  //------------------------------------------------------
  const CardDatenDesktop = ({ vst, evt }) => {
    const [anhangMenuOpen, setAnhangMenuOpen] = useState(false);
    const anhangButtonRef = useRef(null);

    return (
      <TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography>{t("Termin")}</Typography>
              </TableCell>
              <TableCell>
                <Typography>{vst.terminText}</Typography>
                <Typography>{vst.zeitText + " - " + vst.endeText}</Typography>
              </TableCell>
              <TableCell>
                <IconButton
                  color="primary"
                  onClick={() => {
                    onAction("termin_download", vst);
                  }}
                >
                  <EventIcon />
                </IconButton>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>{t("Dauer")}</Typography>
              </TableCell>
              <TableCell>
                <Typography>{vst.dauer + " Min."}</Typography>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            {vst.plz && (
              <TableRow>
                <TableCell>
                  <Typography>{t("Veranstaltungsort")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{vst.str + " " + vst.hnr}</Typography>
                  <Typography>{vst.plz + " " + vst.ort}</Typography>
                  <Typography>{vst.ldkzText}</Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      window.open(
                        "https://www.google.com/maps/search/?api=1&query=" + vst.ldkzText + "+" + vst.plz + "+" + vst.ort + "+" + vst.str + "+" + vst.hnr,
                      );
                    }}
                  >
                    <LocationOnIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}

            {vst?.anhang?.length > 0 && (
              <TableRow>
                <TableCell>
                  <Typography>{vst.anhang.length === 1 ? t("Anhang") : t("Anhänge")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color="text.secondary" sx={{ wordBreak: "break-word" }}>
                    {vst.anhang.length === 1 ? vst.anhang[0].name : `${vst.anhang.length} ${t("Dateien")}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  {vst.anhang.length === 1 ? (
                    <IconButton
                      color="primary"
                      aria-label={t("Anhang anzeigen")}
                      onClick={() => onAction("einladung_download", { vstnr: vst.vstnr, docnr: vst.anhang[0].docnr, fileName: vst.anhang[0].name })}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  ) : (
                    <>
                      <IconButton
                        ref={anhangButtonRef}
                        color="primary"
                        aria-label={t("Anhänge anzeigen")}
                        aria-haspopup="menu"
                        aria-controls={anhangMenuOpen ? "anhang-menu" : undefined}
                        aria-expanded={anhangMenuOpen ? "true" : undefined}
                        onClick={() => setAnhangMenuOpen(true)}
                      >
                        <ExpandMore />
                      </IconButton>
                      <Menu
                        id="anhang-menu"
                        anchorEl={() => anhangButtonRef.current}
                        open={anhangMenuOpen}
                        onClose={() => setAnhangMenuOpen(false)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        slotProps={{ paper: { sx: { maxHeight: 320, maxWidth: 400 } } }}
                      >
                        {vst.anhang.map((datei) => (
                          <MenuItem
                            key={`${datei.docnr}-${datei.key}`}
                            onClick={() => {
                              setAnhangMenuOpen(false);
                              onAction("einladung_download", { vstnr: vst.vstnr, docnr: datei.docnr, fileName: datei.name });
                            }}
                          >
                            <ListItemIcon>
                              <VisibilityIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={datei.name} primaryTypographyProps={{ sx: { whiteSpace: "normal", wordBreak: "break-word" } }} />
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell>
                <Typography>{t("Anmeldungen")}</Typography>
              </TableCell>
              <TableCell>
                <Typography>{vst.anzahlTeilnehmer + (evt.tnMax === 0 ? "" : " (max." + evt.tnMax + t(" Teilnehmer"))}</Typography>

                {vst.anmeldung && (
                  <Typography color={["0", "1", "5", "9"].includes(vst.anmeldung) ? "green" : "red"}>{vst.anmeldung && vst.anmeldungText}</Typography>
                )}

                {!vst.anmeldung && evt.tnMax > 0 && vst.anzahlTeilnehmer >= evt.tnMax && <Typography color="red">maximale Teilnehmerzahl erreicht</Typography>}
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>{t("Verantwortlicher")}</Typography>
              </TableCell>
              <TableCell>
                <Typography>{evt.kontaktName}</Typography>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>{t("Leiter/Trainer")}</Typography>
              </TableCell>
              <TableCell>
                <Typography>{vst.kontaktName}</Typography>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Details zu einem Event wenn es keine Veranstaltung gibt
  const EventDetails = ({ evt }) => {
    return (
      <div className="pt-2">
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>{"Warteliste"}</TableCell>
                <TableCell>{evt.anzahlWarteliste}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{"Teilnehmerbegrenzung"}</TableCell>
                <TableCell>
                  <Typography variant="subtitle2">{"min. " + evt.tnMin}</Typography>
                  <Typography variant="subtitle2">{"max. " + evt.tnMax}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{"Verantwortlicher"}</TableCell>
                <TableCell>
                  <Typography variant="subtitle2">{evt.kontaktName}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  // Wrapper welcher die einzelnen Bereiche des Events sauber aufteilt
  const Wrapper = ({ children, align = "center" }) => {
    return (
      <Box
        sx={{
          height: 100,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: align,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Box sx={{ width: "100%" }}>{children}</Box>
      </Box>
    );
  };

  const EventsDesktop = () => {
    return (
      <Grid container spacing={2} padding={2}>
        {filteredList?.map((evt, index) => {
          return (
            <Grid key={`${evt.key}`} item xs={12} sm={12} md={12} lg={12} xl={6}>
              <Accordion expanded={expanded === "panel" + index} onChange={handleChange("panel" + index)}>
                {/* Wichtigste Event infos */}
                <CustomAccordionSummary expandIcon={<KeyboardArrowRightIcon />} aria-controls={`panel${index}bh-content`} id={`panel${index}bh-header`}>
                  <Box sx={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "flex-start" }}>
                    <Typography sx={{ fontWeight: "bold" }} component="span">
                      {evt.bezeichnung}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 3fr 2fr",
                        gap: 0,
                        width: "100%",
                      }}
                    >
                      <Wrapper justify="flex-start">
                        {evt.faellig && (
                          <Typography color="red" component="span">
                            {"Teilnahme erforderlich"}
                          </Typography>
                        )}
                      </Wrapper>

                      <Wrapper justify="flex-end">
                        {/* Auskommentiert bis wir auch Vorgesetzten Optionen hier einbauen
                        {(evt.kontaktOid === user.id || userAuswahl?.length > 1) && !evt.veranstaltung && (
                          <IconButton
                            variant="icon"
                            tooltip="Warteliste verwalten"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAction("warteliste_verwalten", evt);
                            }}
                          >
                            <GroupIcon />
                          </IconButton>
                        )} */}
                      </Wrapper>

                      <Wrapper justify="flex-end">
                        {evt.veranstaltung ? (
                          <>
                            {evt.veranstaltung.length == 1 && (
                              <Typography color="primary" component="span">
                                {evt.veranstaltung.length + " Veranstaltung verfügbar"}
                              </Typography>
                            )}
                            {evt.veranstaltung.length > 1 && (
                              <Typography color="primary" component="span">
                                {evt.veranstaltung.length + " Veranstaltungen verfügbar"}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography component="span">
                            {"keine Veranstaltung geplant"}
                            {evt.warteliste ? " - auf Warteliste" : ""}
                          </Typography>
                        )}
                      </Wrapper>

                      <Wrapper justify="flex-end">
                        {evt.warteliste != undefined && evt.anmeldung != "3" && (
                          <>
                            {!evt.warteliste && ["0", "2", "5"].includes(evt.anmeldung) && (
                              <IconButton
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAction("interesse", evt.eventnr + "000");
                                }}
                              >
                                <Box sx={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "center" }}>
                                  <AddIcon />
                                  <Typography>Warte-Liste beitreten</Typography>
                                </Box>
                              </IconButton>
                            )}
                          </>
                        )}
                        {evt.warteliste && (
                          <IconButton
                            variant="icon"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAction("abmelden", evt.eventnr + "000");
                            }}
                          >
                            <Box sx={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "center" }}>
                              <CloseIcon />
                              <Typography>Warte-Liste austreten</Typography>
                            </Box>
                          </IconButton>
                        )}
                      </Wrapper>
                    </Box>
                  </Box>
                </CustomAccordionSummary>
                {/* Event Details oder Veranstaltungen */}
                <AccordionDetails>
                  <Divider />
                  {evt?.veranstaltung?.length > 0 ? (
                    evt?.veranstaltung
                      .sort((a, b) => {
                        // Prüfen ob wir Status 0 haben (offene Veranstaltung)
                        const isZeroA = a?.status == 0;
                        const isZeroB = b?.status == 0;
                        if (isZeroA !== isZeroB) return isZeroA ? -1 : 1; //Die sollen immer zuerst kommen

                        // Alle anderen noch nach Datum sortieren und auhc alle mit status 0 nach Datum sortieren
                        const terminA = a?.termin || "";
                        const terminB = b?.termin || "";
                        return terminB.localeCompare(terminA);
                      })
                      .map((veranstaltung, index) => {
                        return <VeranstaltungCard key={`${veranstaltung.key}`} vst={veranstaltung} evt={evt}></VeranstaltungCard>;
                      })
                  ) : (
                    <EventDetails evt={evt} />
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // Veranstaltungs Infos
  const VeranstaltungCard = ({ vst, evt }) => {
    return (
      <Card sx={{ marginBottom: 1, marginTop: 1, padding: 0 }}>
        <CardHeader
          title={
            <>
              <Box sx={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between" }}>
                <Box sx={[]}>
                  <Typography sx={{ fontWeight: "bold" }}>{vst.info}</Typography>
                </Box>
                <Box sx={[]}>
                  <Typography color={vst.status === "0" ? "orange" : vst.status === "1" ? "green" : "red"}>{vst.statusText}</Typography>
                  <Typography color={vst.status === "0" ? "orange" : vst.status === "1" ? "green" : "red"}>
                    {vst.anmeldung && " - " + vst.anmeldungText}
                  </Typography>
                </Box>
              </Box>
            </>
          }
        />
        <CardContent>
          {/* Daten  */}
          <Box sx={{ display: { xs: "none", sm: "none", md: "block" } }}>
            <CardDatenDesktop vst={vst} evt={evt} />
          </Box>
        </CardContent>

        <CardActions>
          <>
            <Box sx={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between" }}>
              <Box sx={[]}>
                {/* Auskommentiert bis wir auch Vorgesetzten Optionen hier einbauen
                {(vst.kontaktOid === user.id || userAuswahl?.length > 1) && (
                  <IconButton
                    variant="icon"
                    color="primary"
                    tooltip="Teilnehmer verwalten"
                    onClick={() => {
                      onAction("teilnehmer_verwalten", vst);
                    }}
                  >
                    <Typography>{"Teilnehmer"}</Typography>
                    <GroupIcon />
                  </IconButton>
                )} */}
              </Box>
              <Box sx={[]}>
                {!["2", "3"].includes(evt.anmeldung) && vst.status === "0" && (
                  <>
                    {!vst.anmeldung && (
                      <IconButton
                        variant="icon"
                        color="primary"
                        tooltip="an Veranstaltung teilnehmen"
                        onClick={() => {
                          onAction("anmelden", vst.vstnr);
                        }}
                      >
                        <AddIcon />
                        <Typography>{"Teilnehmen"}</Typography>
                      </IconButton>
                    )}

                    {vst.anmeldung && (
                      <IconButton
                        variant="icon"
                        color="error"
                        tooltip="abmelden"
                        onClick={() => {
                          onAction("abmelden", vst.vstnr);
                        }}
                      >
                        <CloseIcon />
                        <Typography>{"Abmelden"}</Typography>
                      </IconButton>
                    )}
                  </>
                )}

                {vst.anmeldung != "3" && (
                  // Interesse / Teilnahme
                  <>
                    {evt.anmeldung === "3" && vst.status === "0" && (
                      <>
                        {vst.anmeldung !== "0" && (
                          <IconButton
                            variant="icon"
                            color="primary"
                            tooltip="zu Veranstaltung zusagen"
                            onClick={() => {
                              onAction("anmelden", vst.vstnr);
                            }}
                          >
                            <AddIcon />
                            <Typography>{"Zusagen"}</Typography>
                          </IconButton>
                        )}

                        {vst.anmeldung !== "8" && (
                          <IconButton
                            variant="icon"
                            tooltip="abmelden"
                            color="error"
                            onClick={() => {
                              onAction("abmelden", vst.vstnr);
                            }}
                          >
                            <CloseIcon />
                            <Typography>{"Absagen"}</Typography>
                          </IconButton>
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {vst.status === "1" && vst.anmeldung && !["1", "2"].includes(vst.anmeldung) && (
              // teilgenommen / nicht teilgenommen
              <>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography>Haben Sie teilgenommen?</Typography>
                  <Box display="flex" justifyContent="space-between" width="100%">
                    <IconButton
                      variant="icon"
                      tooltip="Teilnahme bestätigen"
                      color="primary"
                      onClick={() => {
                        onAction("teilgenommen", vst.vstnr);
                      }}
                    >
                      <CheckIcon />
                    </IconButton>

                    <IconButton
                      variant="icon"
                      tooltip="nicht teilgenommen"
                      color="error"
                      onClick={() => {
                        onAction("gefehlt", vst.vstnr);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </>
        </CardActions>
      </Card>
    );
  };

  return (
    <Layout fullWidth>
      <Box sx={{ paddingTop: 4, width: "90%" }}>
        {/* Desktop Filter */}
        <Box sx={{ display: { xs: "none", sm: "none", md: "block" }, width: "100%" }}>
          <Grid container justifyContent="flex-start" spacing={1} sx={{ width: "100%" }} style={{ paddingTop: 20 }}>
            <Grid item xs={12} md={3}>
              <Box marginLeft={2}>
                <SearchTextField
                  size="small"
                  fullWidth
                  label={t("Suche")}
                  id="filter"
                  variant="outlined"
                  value={suche}
                  inputProps={{
                    autoCapitalize: "off",
                    autoCorrect: "off",
                    spellCheck: "false",
                    autoComplete: "off",
                  }}
                  onChange={(e) => setSuche(e.target.value)}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                multiple
                fullWidth
                onChange={(event, newValue) => {
                  setEventArt(newValue);
                }}
                value={eventArt}
                size="small"
                id="tags-outlined"
                options={eventArtOptions}
                disabled={!eventArtOptions}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => <TextField {...params} label="Nach Event Art filtern" placeholder="Eventart" />}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                onChange={(event, newValue) => {
                  setPflichtVeranstaltung(newValue);
                }}
                value={pflichtVeranstaltung}
                size="small"
                fullWidth
                id="Pflichtveranstaltungen"
                options={pflichtVeranstaltungOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => <TextField {...params} label="Pflichtveranstaltungen" placeholder="Pflichtveranstaltung" />}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <DatePicker
                label=""
                value={termin}
                onChange={(newValue) => {
                  setTermin(newValue);
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    height: "0.45rem",
                  },
                }}
                slotProps={{
                  field: { clearable: true, onClear: () => setTermin() },
                }}
              />
            </Grid>

            <Grid item xs={12} md={3} justifyContent="flex-end">
              <IconButton
                variant="icon"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onAction("reload", "");
                }}
              >
                <AutorenewIcon></AutorenewIcon>
              </IconButton>
            </Grid>
          </Grid>
        </Box>

        {/* Mobile Filter*/}
        <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
          <Box display="flex" alignItems={"center"}>
            <SearchTextField
              size="small"
              fullWidth
              label={t("Suche")}
              id="filter"
              variant="outlined"
              sx={{ marginLeft: "16px" }}
              value={suche}
              inputProps={{
                autoCapitalize: "off",
                autoCorrect: "off",
                spellCheck: "false",
                autoComplete: "off",
              }}
              onChange={(e) => setSuche(e.target.value)}
            />

            <Box pr={1}>
              <Tooltip title="Filter">
                <IconButton color="primary" onClick={() => setFilterDialog(true)}>
                  <FilterList />
                </IconButton>
              </Tooltip>
            </Box>
            <Box pr={1}>
              <IconButton
                variant="icon"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onAction("reload", "");
                }}
              >
                <AutorenewIcon></AutorenewIcon>
              </IconButton>
            </Box>

            <Dialog open={filterDialog} onClose={() => setFilterDialog(false)} maxWidth="md" fullWidth>
              <DialogTitle>Filter</DialogTitle>
              <DialogContent>
                <Grid container justifyContent="center" spacing={1} style={{ paddingTop: 20 }}>
                  <Grid item xs={12} md={5}></Grid>

                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      multiple
                      onChange={(event, newValue) => {
                        setEventArt(newValue);
                      }}
                      value={eventArt}
                      size="small"
                      id="tags-outlined"
                      options={eventArtOptions}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Nach Event Art filtern" placeholder="Eventart" />}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Autocomplete
                      onChange={(event, newValue) => {
                        setPflichtVeranstaltung(newValue);
                      }}
                      value={pflichtVeranstaltung}
                      size="small"
                      id="Pflichtveranstaltungen"
                      options={pflichtVeranstaltungOptions}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => <TextField {...params} label="Pflichtveranstaltungen" placeholder="Pflichtveranstaltung" />}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DatePicker
                        label="Termine nach"
                        value={termin}
                        onChange={(newValue) => setTermin(newValue)}
                        sx={{
                          flexGrow: 1,
                          "& .MuiInputBase-input": {
                            height: "0.45rem",
                          },
                        }}
                      />
                      <IconButton onClick={() => setTermin(null)}>
                        <Cancel />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button style={{ marginRight: "10px" }} variant="secondary" display="flex" onClick={() => setFilterDialog(false)}>
                  {t("Abbrechen")}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>
      </Box>
      {/* Anzeige */}
      <Box sx={{ display: { xs: "none", sm: "none", md: "block" }, width: "100%" }}>
        <EventsDesktop />
      </Box>

      <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
        <EventsMobile />
        <DetailsEvt open={mobileDetails} onClose={() => setMobileDetails(false)} onOpen={() => setMobileDetails(true)}></DetailsEvt>
      </Box>
    </Layout>
  );
};

export default VstUebersicht;
